/**
 * Cache keys, in one place.
 *
 * The shared `queryKeys` helper in `@erpx/ui` returns a union (with or without
 * a params segment) that TanStack's generics cannot narrow at a call site, so
 * this app declares its own fixed-shape keys. The value of having them here is
 * the same either way: a mutation invalidating `['users']` and a list querying
 * `['users', 'list', …]` must agree, and they only reliably agree when neither
 * is a string literal typed out twice.
 */

export const QUERY_KEYS = {
  /** Prefix used by invalidations after any user mutation. */
  users: ['users'] as const,
  usersPage: (params: Record<string, unknown>) => ['users', 'list', params] as const,
  /** The whole directory, walked to the end of the cursor. */
  usersAll: ['users', 'all'] as const,
  roles: ['roles', 'list'] as const,
  sessions: ['sessions'] as const,
  sessionsList: ['sessions', 'list'] as const,
  /** Prefix invalidated after any approval decision. */
  approvals: ['approvals'] as const,
  approvalInbox: (params: Record<string, unknown>) => ['approvals', 'tasks', params] as const,
  approvalRequest: (id: number) => ['approvals', 'request', id] as const,
  auditPage: (filters: Record<string, unknown>) => ['audit', 'list', filters] as const,
  /** The server's closed action vocabulary — immutable for the life of a deploy. */
  auditActions: ['audit', 'actions'] as const,
  auditTimeline: (entity: string, entityId: string) =>
    ['audit', 'timeline', entity, entityId] as const,
} as const
